import { useSyncExternalStore } from 'react';
import { opensQuickViews } from './quick-view-pages';

// Where a task, a deal or a contact opens on a plain click (TF-367): in the side sheet over the page,
// or as a page of its own. Chosen per kind in My profile and kept in this browser, like the language
// and the appearance. Until a person chooses, each opens as it always has (DEFAULT_OPEN_IN, and a deal
// from an account or the search still as a page); once chosen, the choice holds wherever the record is
// clicked. Ctrl+click, a middle click and "Open in new tab" are the browser's and always open the page.
// A phone has no choice: its sheet is the whole screen anyway.

export type RecordKind = 'task' | 'deal' | 'contact';
export type OpenIn = 'sheet' | 'page';

export const RECORD_KINDS: readonly RecordKind[] = ['task', 'deal', 'contact'];
/** What the choice shows before a person makes one: how each kind opens from its own list and board. */
export const DEFAULT_OPEN_IN: Record<RecordKind, OpenIn> = { task: 'sheet', deal: 'sheet', contact: 'page' };

const storageKey = (kind: RecordKind) => `teamfolio.openIn.${kind}`;
/** The address segment of each kind's page, /c/<company>/crm/<segment>/<id>. */
const SEGMENT: Record<RecordKind, string> = { task: 'tasks', deal: 'deals', contact: 'contacts' };

const unsaved = new Map<RecordKind, OpenIn>();
const listeners = new Set<() => void>();

/** The choice a person made for a kind in this browser; null when they have not made one. */
export function chosenOpenIn(kind: RecordKind): OpenIn | null {
  if (unsaved.has(kind)) return unsaved.get(kind)!;
  try {
    const value = localStorage.getItem(storageKey(kind));
    return value === 'sheet' || value === 'page' ? value : null;
  } catch {
    return null;
  }
}

export function setOpenIn(kind: RecordKind, value: OpenIn): void {
  try {
    localStorage.setItem(storageKey(kind), value);
    unsaved.delete(kind);
  } catch {
    // Storage refused (a private window): kept until the page is left.
    unsaved.set(kind, value);
  }
  listeners.forEach((listener) => listener());
}

/** The choice for a kind as My profile shows it: the person's own, else the default. */
export function useOpenIn(kind: RecordKind): OpenIn {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => chosenOpenIn(kind) ?? DEFAULT_OPEN_IN[kind],
    () => DEFAULT_OPEN_IN[kind],
  );
}

/** A phone, where the side sheet is the whole screen and there is nothing to choose (as useCompact). */
export function isPhone(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 599px)').matches;
}

/** Whether a record of this kind should open as a page, wherever it is opened from. */
export function opensAsPage(kind: RecordKind): boolean {
  return !isPhone() && chosenOpenIn(kind) === 'page';
}

/** Whether a link to the record's page should open the side sheet instead. */
export function opensAsSheet(kind: RecordKind): boolean {
  return !isPhone() && chosenOpenIn(kind) === 'sheet';
}

export const recordPage = (tenantId: string, kind: RecordKind, id: string) => `/c/${tenantId}/crm/${SEGMENT[kind]}/${id}`;

/** The section a kind's own list and board are in: there they open as the list or board decides. */
export const recordSection = (tenantId: string, kind: RecordKind) => `/c/${tenantId}/crm/${SEGMENT[kind]}`;

export interface LinkedRecord {
  kind: RecordKind;
  id: string;
  tenantId: string;
  /** The link opens it in the side sheet (?deal=<id> on a page), not as its own page. */
  inSheet: boolean;
}

const PAGE = /^\/c\/([^/]+)\/crm\/(tasks|deals|contacts)\/([^/]+)\/?$/;
const COMPANY = /^\/c\/([^/]+)(?:\/|$)/;

/** The record an address in the app opens, if it opens one: its page, or a page with ?task=, ?deal= or ?contact=. */
export function linkedRecord(pathname: string, search: string): LinkedRecord | null {
  const page = PAGE.exec(pathname);
  if (page) {
    const kind = RECORD_KINDS.find((each) => SEGMENT[each] === page[2])!;
    return { kind, id: decodeURIComponent(page[3]!), tenantId: page[1]!, inSheet: false };
  }
  const company = COMPANY.exec(pathname);
  if (!company) return null;
  const params = new URLSearchParams(search);
  for (const kind of RECORD_KINDS) {
    const id = params.get(kind);
    // ?deal= and ?contact= open a sheet only over the pages it belongs on: on the files page each is the
    // list's filter (TF-385).
    if (id && (kind === 'task' || opensQuickViews(pathname))) return { kind, id, tenantId: company[1]!, inSheet: true };
  }
  return null;
}
